import { DropdownOption } from '@/common/getDropdownList';
import { Can } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { DeleteIcon, EditIcon } from '@/icons';
import { ClubRow, SubGroupRow } from '@/services/groupsManagement/subGroups.types';
import type { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import ManageSubGroupFormContainer from '../../ManageSubGroupFormContainer';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

export function getSubGroupColumns(
  onOpen: OnOpenProps,
  groupId: string,
  openConfirmation: OpenConfirmationProps,
  deleteSubGroup: (payload: { groupId: string; subGroupId: string; status: 'D' }) => Promise<unknown>,
  manageSubGroupClubStatus: (payload: {
    subGroupId: string;
    clubId: string;
    status: 'A' | 'B' | 'D';
  }) => Promise<unknown>,
  t: (key: string) => string,
  locationOptions: DropdownOption[],
  defaultGroupDetailValues?: {
    groupName: string;
    groupNameKatakana: string;
  },
): ColumnDef<SubGroupRow | ClubRow>[] {
  return [
    {
      id: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => {
        if (row.depth > 0) return <div className="max-w-[6rem]" />;

        const topLevelRows = table.getRowModel().rows.filter((r) => r.depth === 0);
        const index = topLevelRows.findIndex((r) => r.id === row.id);

        return (
          <div className="flex items-center gap-1 max-w-[6rem]">
            <span className="font-medium mr-1">{index + 1}</span>
          </div>
        );
      },
      enableHiding: false,
    },

    // ── Sub Group Info (expand toggle + name) ────────────────────────────────
    {
      id: 'subGroupInfo',
      accessorKey: 'subGroupInfo',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.subGroupInfo')} />
      ),
      cell: ({ row }) => {
        // Child rows: this column is empty (club name goes in clubInfo column)
        if (row.depth > 0) return <div />;

        const { subGroupInfo } = row.original as SubGroupRow;
        return (
          <div style={{ paddingLeft: `${row.depth * 1.5}rem` }} className="flex items-center ">
            {row.getCanExpand() && (
              <button
                type="button"
                onClick={row.getToggleExpandedHandler()}
                className="mr-2 cursor-pointer text-foreground-subdued"
              >
                {row.getIsExpanded() ? '▼' : '▶'}
              </button>
            )}
            <span>{subGroupInfo}</span>
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.subGroupInfo',
      },
    },

    // ── Total Clubs ──────────────────────────────────────────────────────────
    {
      id: 'totalClub',
      accessorKey: 'totalClub',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalClubs')} />,
      cell: ({ row }) => {
        if (row.depth > 0) return <div className="text-muted-foreground"></div>;
        const original = row.original as SubGroupRow;
        // "Default" group has no club count in the design
        return <div className="font-medium">{original.totalClub ?? '-'}</div>;
      },
      enableHiding: true,
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.totalClubs',
      },
    },

    // ── Club / Store Info (avatar + name) ────────────────────────────────────
    {
      id: 'clubInfo',
      accessorKey: 'clubInfo',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.clubStoreInfo')} />
      ),
      enableSorting: false,
      cell: ({ row }) => {
        // Top-level subgroup rows: nothing here
        if (row.depth === 0) return <div className="text-muted-foreground"></div>;

        const { clubInfo, clubInfoEng, clubProfile } = row.original as ClubRow;

        if (!clubInfo && !clubInfoEng) {
          return <div className="text-muted-foreground text-sm pl-6">—</div>;
        }

        return (
          <div className="flex items-center gap-3">
            <img
              src={clubProfile?.path}
              alt={clubProfile?.alt ?? 'club profile'}
              className="h-8 w-8 rounded-full object-cover shrink-0 border border-border"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src =
                  'https://s3.ap-northeast-1.amazonaws.com/dev-assets.hoslog.jp/ADMIN/no-image.jpeg';
              }}
            />
            <div className="flex flex-col min-w-0">
              <span className="font-medium text-sm truncate text-primary">{clubInfoEng ?? clubInfo}</span>
              <span className="text-xs text-muted-foreground truncate">{clubInfo}</span>
            </div>
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.clubStoreInfo',
      },
      enableHiding: true,
    },

    // ── Location ─────────────────────────────────────────────────────────────
    {
      id: 'location',
      accessorKey: 'location',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.location')} />,
      enableSorting: false,
      cell: ({ row }) => {
        // Top-level subgroup rows: nothing here
        if (row.depth === 0) return <div />;

        const { location } = row.original as ClubRow;
        return (
          <div className="text-sm text-foreground">{location ?? <span className="text-muted-foreground">—</span>}</div>
        );
      },
      meta: {
        label: t('common.page.tableColumn.location'),
        options: locationOptions,
        hideColumnLabelKey: 'common.page.tableColumn.location',
      },
      enableHiding: true,
    },

    // ── Actions ──────────────────────────────────────────────────────────────
    {
      id: 'actions',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.actions')} />,
      cell: ({ row }) => {
        const original = row.original as SubGroupRow;
        const isDefault = original.subGroupInfo === 'Default';
        const hasClubs = (original.totalClub ?? 0) > 0;

        // Top-level subgroup row actions
        if (row.depth === 0) {
          return (
            <div className="flex  justify-center">
              {!isDefault && (
                <Can rule={{ allOf: [PERMS.group.manage.subgroup] }}>
                  <Button
                    variant={'ghost'}
                    className="size-4"
                    onClick={() =>
                      onOpen({
                        title: 'Manage Groups',
                        okText: <CrumbTitle id="common.page.modal.submit" />,
                        size: 'lg',
                        modalTitle: 'configurationManagement.groupsManagement.manageGroups.title',
                        component: (
                          <ManageSubGroupFormContainer
                            groupId={groupId}
                            defaultValues={defaultGroupDetailValues}
                            subGroupId={original.subgroupId}
                          />
                        ),
                      })
                    }
                  >
                    <EditIcon className="size-4 text-foreground-subtle" />
                  </Button>
                </Can>
              )}
              <Can rule={{ allOf: [PERMS.group.update.subgroup.status] }}>
                <Button
                  variant="ghost"
                  disabled={hasClubs}
                  className={` size-4 ${hasClubs} ? 'opacity-40  cursor-not-allowed' : ''`}
                  onClick={() => {
                    if (hasClubs) return;

                    openConfirmation({
                      title: t('common.page.modal.confirmation.title'),
                      description: t('common.page.modal.deleteItemMessage'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.confirm'),
                      onAction: async () => {
                        await deleteSubGroup({
                          groupId,
                          subGroupId: original.subgroupId,
                          status: 'D',
                        });
                      },
                      onCancel: () => {},
                      size: 'lg',
                      variant: 'destructive',
                    });
                  }}
                >
                  <DeleteIcon className={`size-4 ${hasClubs ? 'text-muted-foreground' : 'text-destructive-text'}`} />
                </Button>
              </Can>
            </div>
          );
        }

        // Child club row actions
        const club = row.original as ClubRow;
        const subGroup = row.getParentRow()?.original as SubGroupRow | undefined;

        return (
          <div className="flex gap-4 justify-center">
            <Can rule={{ allOf: [PERMS.group.manage.subgroupClubStatus] }}>
              <Button
                variant="ghost"
                className="p-0 m-0"
                onClick={() => {
                  if (!subGroup) return;

                  openConfirmation({
                    title: t('common.page.modal.confirmation.title'),
                    description: t('common.page.modal.deleteItemMessage'),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.confirm'),
                    onAction: async () => {
                      await manageSubGroupClubStatus({
                        subGroupId: subGroup.subgroupId,
                        clubId: club.clubId,
                        status: 'D',
                      });
                    },
                    onCancel: () => {},
                    size: 'lg',
                    variant: 'destructive',
                  });
                }}
              >
                <DeleteIcon className="size-4 text-destructive-text" />
              </Button>
            </Can>
          </div>
        );
      },
      size: 40,
    },
  ];
}
